import { createSelector } from '@reduxjs/toolkit';
import { sumBy } from 'lodash';

import { InsightsState } from 'app-redux/InsightsState';
import { formatISO } from 'utils/dateTimeHelpers';

import { LocationSearchType } from 'components/_controls/AoiSearch/models';
import { ChipOptions } from 'components/_controls/BdChip/BdChip';

import { doesODLocationPairBubble, getConnectivityGroupByTypes } from './connectivity.helpers';

const travelVolumesSelector = (state: InsightsState) => state?.connectivity?.travelVolumes;

export const originLocationSelector = (state: InsightsState) => state?.connectivity?.originLocation;
export const destinationLocationSelector = (state: InsightsState) => state?.connectivity?.destinationLocation;
export const isLoadingDiseaseRisksSelector = (state: InsightsState) => state?.connectivity?.loadingDiseaseRisks;
export const isLoadingTravelVolumesSelector = (state: InsightsState) => state?.connectivity?.loadingTravelVolumes;
export const isLoadingODLocationsSelector = (state: InsightsState) => state?.connectivity?.loadingLocationsInfo;
export const isLoadingAggRisksSelector = (state: InsightsState) => state?.connectivity?.loadingModAggRow;
export const activeMopSortSelector = (state: InsightsState) => state?.connectivity?.activeMopSort;
export const activeModSortSelector = (state: InsightsState) => state?.connectivity?.activeModSort;
export const activeOriginGroupBySelector = (state: InsightsState) => state?.connectivity?.activeOriginGroupBy;
export const activeDestinationGroupBySelector = (state: InsightsState) => state?.connectivity?.activeDestinationGroupBy;
export const groupByDisclaimerStatusSelector = (state: InsightsState) => state?.connectivity?.groupByProvinceDisclaimerStatus;
export const isDomesticIncluded = (state: InsightsState) => state?.connectivity?.includeDomestic;
export const isGroundTravelIncludedSelector = (state: InsightsState) => state?.connectivity?.includeGroundTravel;
export const isGroundTravelAllowed = (state: InsightsState) => state?.connectivity?.isGroundTravelAllowed;
export const aggregatedRisksSelector = (state: InsightsState) => state?.connectivity?.modAggVolumes ?? [];
export const diseaseRisksSelector = (state: InsightsState) => state?.connectivity?.diseaseRisks ?? [];
export const allDiseasesSelector = (state: InsightsState) => state?.connectivity?.allModDiseases;
export const filteredDiseasesSelector = (state: InsightsState) => state?.connectivity?.filteredDiseaseIds;
export const totalDiseaseRiskRowsSelector = (state: InsightsState) => state?.connectivity?.diseaseRisksAvail;

export const filteredDiseasesAsChips = createSelector(filteredDiseasesSelector, allDiseasesSelector, (filteredIds, diseases) =>
  filteredIds.map(dId => ({ id: dId, label: diseases[dId] } as ChipOptions))
);
export const isDomesticAllowed = createSelector(
  originLocationSelector,
  destinationLocationSelector,
  (origin, destination) => origin == null || destination == null || origin?.countryId === destination?.countryId
);
export const isDomesticOnly = createSelector(
  originLocationSelector,
  destinationLocationSelector,
  (origin, destination) => origin != null && destination != null && origin?.countryId === destination?.countryId
);
export const noLocationsAdded = createSelector(
  originLocationSelector,
  destinationLocationSelector,
  (origin, destination) => origin == null && destination == null
);
export const isTravelVolumesEmpty = createSelector(
  noLocationsAdded,
  travelVolumesSelector,
  (noLocations, tv) => noLocations || !tv?.travelVolumes?.length
);
export const displayNoGroundTravelMessage = createSelector(
  originLocationSelector,
  destinationLocationSelector,
  (origin, destination) => origin?.locationType === LocationSearchType.Airport || destination?.locationType === LocationSearchType.Airport
);

export const isDiseaseRisksEmpty = createSelector(noLocationsAdded, diseaseRisksSelector, (noLocations, dr) => noLocations || !dr?.length);
export const getTravelVolumeDate = createSelector(travelVolumesSelector, tv => tv?.date);
export const getNumOfVisibileTravelVols = createSelector(travelVolumesSelector, tv => tv?.travelVolumes?.length ?? 0);
export const getTotalTravelVolRows = createSelector(travelVolumesSelector, tv => tv?.total);
export const getTravelVolRows = createSelector(travelVolumesSelector, tv => tv?.travelVolumes ?? []);
export const getAggregatedAirTravelVolume = createSelector(travelVolumesSelector, tv => tv?.aggregatedAirVolume);
export const getAggregatedGroundTravelVolume = createSelector(travelVolumesSelector, tv => tv?.aggregatedGroundVolume);
export const allRowsLoaded = createSelector(getNumOfVisibileTravelVols, getTotalTravelVolRows, (visible, total) => visible >= total);
export const getOriginLocationId = createSelector(originLocationSelector, loc => loc?.id);
export const getDestinationLocationId = createSelector(destinationLocationSelector, loc => loc?.id);
export const getOriginLocationType = createSelector(originLocationSelector, loc => loc?.locationType);
export const getDestinationLocationType = createSelector(destinationLocationSelector, loc => loc?.locationType);

export const groundTravelDisabledText = createSelector(isGroundTravelAllowed, groundTravelAllowed =>
  groundTravelAllowed ? undefined : 'Ground travel data is unavailable for this location.'
);
export const getNumOfUnloadedTravelVolRows = createSelector(
  getTotalTravelVolRows,
  getNumOfVisibileTravelVols,
  (totalRows, visibleRows) => totalRows - visibleRows
);
export const getNumOfVisibleDiseaseRiskRows = createSelector(diseaseRisksSelector, dr => dr?.length ?? 0);
export const getNumOfUnloadedDiseaseRiskRows = createSelector(
  totalDiseaseRiskRowsSelector,
  getNumOfVisibleDiseaseRiskRows,
  (totalRows, visbileRows) => totalRows - visbileRows
);
export const getTotalDiseaseRiskSelector = createSelector(aggregatedRisksSelector, aggRisks => sumBy(aggRisks, r => r.volume));
export const getOriginGroupByOptions = createSelector(
  originLocationSelector,
  destinationLocationSelector,
  isDomesticIncluded,
  (origin, destination, domesticIncluded) => getConnectivityGroupByTypes(origin, destination, domesticIncluded)
);
export const getDestinationGroupByOptions = createSelector(
  destinationLocationSelector,
  originLocationSelector,
  isDomesticIncluded,
  (destination, origin, domesticIncluded) => getConnectivityGroupByTypes(destination, origin, domesticIncluded)
);
export const getFormattedTravelVolDate = createSelector(getTravelVolumeDate, date => (date == null ? '' : formatISO(date, 'MMMM yyyy')));
export const shouldShowMopGroupByDisclaimer = createSelector(
  groupByDisclaimerStatusSelector,
  activeOriginGroupBySelector,
  activeDestinationGroupBySelector,
  getTravelVolRows,
  (disclaimerStatus, originGroupBy, destinationGroupBy, rows) => {
    if (disclaimerStatus === 'hidden' || disclaimerStatus === 'user-hidden') return false;

    return rows.some(row => doesODLocationPairBubble(row.sourceTravelLocation, originGroupBy, row.destinationTravelLocation, destinationGroupBy));
  }
);
export const shouldShowModGroupByDisclaimer = createSelector(
  groupByDisclaimerStatusSelector,
  activeOriginGroupBySelector,
  activeDestinationGroupBySelector,
  diseaseRisksSelector,
  (disclaimerStatus, originGroupBy, destinationGroupBy, rows) => {
    if (disclaimerStatus === 'hidden' || disclaimerStatus === 'user-hidden') return false;

    return rows.some(row => doesODLocationPairBubble(row.sourceTravelLocation, originGroupBy, row.destinationTravelLocation, destinationGroupBy));
  }
);
